import React from 'react';

export function KneadingTechniques() {
  return (
    <div className="space-y-6 text-white/90" style={{ userSelect: "none" }}>
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Kneading Techniques</h3>
        <p className="mb-4">
          Kneading is a fundamental bread-making technique that develops gluten structure, which gives bread its characteristic texture and ability to trap gas. Different kneading methods are suited to different dough types and hydration levels. This guide covers traditional and modern techniques for optimal dough development.
        </p>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Traditional Kneading Methods</h3>
        
        <div className="space-y-6">
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Conventional Bench Kneading</h4>
            <p className="text-sm text-white/80 mt-1">
              The classic push-fold-turn method performed on a work surface.
            </p>
            <div className="mt-3">
              <h5 className="text-amber-400 text-sm">Technique:</h5>
              <ol className="list-decimal list-inside text-sm text-white/80 mt-1 space-y-1">
                <li>Push dough away with heel of hand</li>
                <li>Fold dough in half toward you</li>
                <li>Rotate dough 90 degrees</li>
                <li>Repeat for 8-12 minutes until smooth and elastic</li>
              </ol>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Best For:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Low to medium hydration doughs (55-65%)</li>
                  <li>Enriched doughs with butter/eggs</li>
                  <li>Beginners learning to feel dough development</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Advantages:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>No special equipment needed</li>
                  <li>Provides tactile feedback</li>
                  <li>Good control over dough temperature</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">French Fold (Slap and Fold)</h4>
            <p className="text-sm text-white/80 mt-1">
              A more vigorous technique that rapidly develops gluten in wetter doughs.
            </p>
            <div className="mt-3">
              <h5 className="text-amber-400 text-sm">Technique:</h5>
              <ol className="list-decimal list-inside text-sm text-white/80 mt-1 space-y-1">
                <li>Pick up dough with both hands</li>
                <li>Slap it onto the counter away from you</li>
                <li>Stretch and fold it back over itself</li>
                <li>Rotate 90 degrees and repeat</li>
                <li>Continue for 3-5 minutes until dough becomes cohesive</li>
              </ol>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Best For:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Medium to high hydration doughs (65-80%)</li>
                  <li>Sticky doughs that are difficult to handle</li>
                  <li>Rapid gluten development</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Advantages:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Quickly transforms sticky dough into manageable dough</li>
                  <li>Develops strong gluten network</li>
                  <li>Incorporates air into the dough</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Rubaud Method</h4>
            <p className="text-sm text-white/80 mt-1">
              A gentle but effective technique developed by French baker Gérard Rubaud.
            </p>
            <div className="mt-3">
              <h5 className="text-amber-400 text-sm">Technique:</h5>
              <ol className="list-decimal list-inside text-sm text-white/80 mt-1 space-y-1">
                <li>Place dough in a wide bowl or container</li>
                <li>With one hand, lift and fold the dough while the other hand supports the container</li>
                <li>Create a circular motion, continuously lifting from the bottom and folding over the top</li>
                <li>Continue for 2-5 minutes until dough becomes smoother</li>
              </ol>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Best For:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Medium to high hydration doughs (65-85%)</li>
                  <li>Initial mixing after autolyse</li>
                  <li>Incorporating levain or additional ingredients</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Advantages:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Gentle on the dough structure</li>
                  <li>Maintains dough temperature</li>
                  <li>Less messy than bench techniques</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">No-Knead and Minimal Kneading Techniques</h3>
        
        <div className="space-y-6">
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Stretch and Fold</h4>
            <p className="text-sm text-white/80 mt-1">
              A gentle technique that develops gluten gradually during bulk fermentation.
            </p>
            <div className="mt-3">
              <h5 className="text-amber-400 text-sm">Technique:</h5>
              <ol className="list-decimal list-inside text-sm text-white/80 mt-1 space-y-1">
                <li>With wet hands, grab one side of the dough</li>
                <li>Stretch it upward and fold over to the opposite side</li>
                <li>Rotate the container 90 degrees</li>
                <li>Repeat for all four sides</li>
                <li>Perform 4-6 sets of stretch and folds at 30-45 minute intervals during bulk fermentation</li>
              </ol>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Best For:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Medium to high hydration doughs (65-85%)</li>
                  <li>Preserving delicate dough structure</li>
                  <li>Developing open crumb structure</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Advantages:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Minimal physical effort</li>
                  <li>Preserves gas in the dough</li>
                  <li>Builds strength while maintaining extensibility</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Coil Folds</h4>
            <p className="text-sm text-white/80 mt-1">
              A modern technique that's especially good for very wet doughs.
            </p>
            <div className="mt-3">
              <h5 className="text-amber-400 text-sm">Technique:</h5>
              <ol className="list-decimal list-inside text-sm text-white/80 mt-1 space-y-1">
                <li>With wet hands, gently lift the dough from the center</li>
                <li>Allow the edges to release and fold under (coil) as you lift</li>
                <li>Place the dough back down</li>
                <li>Rotate 90 degrees and repeat</li>
                <li>Perform 4-6 sets during bulk fermentation</li>
              </ol>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Best For:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>High hydration doughs (75-90%)</li>
                  <li>Delicate doughs with open crumb</li>
                  <li>Doughs that are difficult to handle</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Advantages:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Minimal dough handling</li>
                  <li>Preserves gas bubbles</li>
                  <li>Creates tension without degassing</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Lamination</h4>
            <p className="text-sm text-white/80 mt-1">
              A technique that stretches dough into a thin sheet before folding it back onto itself.
            </p>
            <div className="mt-3">
              <h5 className="text-amber-400 text-sm">Technique:</h5>
              <ol className="list-decimal list-inside text-sm text-white/80 mt-1 space-y-1">
                <li>On a wet work surface, gently stretch dough into a large, thin rectangle</li>
                <li>Stretch until nearly transparent without tearing</li>
                <li>Fold the dough in thirds like a letter</li>
                <li>Fold in thirds again to create a small package</li>
                <li>Return to container for continued fermentation</li>
              </ol>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Best For:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>High hydration doughs (75-90%)</li>
                  <li>Incorporating inclusions (nuts, seeds, dried fruit)</li>
                  <li>Creating layered structure</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Advantages:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Develops gluten in all directions</li>
                  <li>Creates organized gluten structure</li>
                  <li>Evenly distributes inclusions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Choosing the Right Technique</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-white/80">
            <thead>
              <tr className="border-b border-white/20">
                <th className="text-left py-3 px-4 text-amber-400">Dough Type</th>
                <th className="text-left py-3 px-4 text-amber-400">Hydration</th>
                <th className="text-left py-3 px-4 text-amber-400">Recommended Techniques</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Enriched Dough (with fat)</td>
                <td className="py-3 px-4">55-65%</td>
                <td className="py-3 px-4">Conventional kneading, Stand mixer</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Basic Lean Dough</td>
                <td className="py-3 px-4">65-70%</td>
                <td className="py-3 px-4">Conventional kneading, French fold, Stretch and fold</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Artisan Sourdough</td>
                <td className="py-3 px-4">70-80%</td>
                <td className="py-3 px-4">Rubaud method, Stretch and fold, Coil folds</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">High Hydration</td>
                <td className="py-3 px-4">80-90%</td>
                <td className="py-3 px-4">Coil folds, Lamination, Minimal handling</td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-medium">Whole Grain Dough</td>
                <td className="py-3 px-4">75-85%</td>
                <td className="py-3 px-4">Stretch and fold, Lamination, Gentle handling</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-amber-400 mb-4">Assessing Gluten Development</h3>
        
        <div className="space-y-4">
          <p className="text-white/80">
            Knowing when your dough has been sufficiently kneaded is crucial. Here are methods to assess gluten development:
          </p>
          
          <div>
            <h4 className="text-lg text-white mb-2">The Windowpane Test</h4>
            <ol className="list-decimal list-inside text-sm text-white/80 space-y-1">
              <li>Take a small piece of dough (about the size of a golf ball)</li>
              <li>Gently stretch it between your fingers</li>
              <li>If it stretches thin enough to see light through without tearing, gluten is well-developed</li>
              <li>If it tears easily, continue kneading</li>
            </ol>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Visual and Tactile Cues</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Well-developed dough is smooth, not rough or shaggy</li>
              <li>It feels elastic and springs back when poked</li>
              <li>The surface becomes taut and slightly shiny</li>
              <li>It pulls away cleanly from the sides of the bowl</li>
              <li>The dough holds its shape rather than spreading excessively</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Common Mistakes</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Underkneading: Dough lacks structure, resulting in dense bread</li>
              <li>Overkneading: Dough becomes tough and loses extensibility (rare with hand kneading, more common with machines)</li>
              <li>Adding too much flour during kneading, resulting in dry dough</li>
              <li>Using the wrong technique for your dough hydration</li>
              <li>Not allowing sufficient rest periods between folds for high-hydration doughs</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}